import {
  Box,
  Card,
  Typography,
  alpha,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTheme } from "@mui/material";
import { OutputDeliveryData, OutputTableData } from "../types";

interface OutputBenefitChartProps {
  chartData: OutputDeliveryData[];
  tableData: OutputTableData[];
}

export const OutputBenefitChart = ({
  chartData,
  tableData,
}: OutputBenefitChartProps) => {
  const theme = useTheme();

  const deliveredData = chartData.map((item) => item.delivered);

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "action.hover",
        }}
      >
        <Typography variant="subtitle2">Output & Benefit Delivery</Typography>
        <Typography variant="caption" color="text.secondary">
          Physical delivery progress and monetised benefits
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
          <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                maxHeight: { xs: 300, md: "100%" },
              }}
            >
              <BarChart
                layout="horizontal"
                xAxis={[
                  {
                    scaleType: "linear",
                    min: 0,
                    max: 100,
                    colorMap: {
                      type: "piecewise",
                      thresholds: [50, 70],
                      colors: [
                        theme.palette.error.main,
                        theme.palette.warning.main,
                        theme.palette.success.main,
                      ],
                    },
                    valueFormatter: (value: number) => `${value}%`,
                    height: 30,
                  },
                ]}
                yAxis={[
                  {
                    scaleType: "band",
                    data: chartData.map((item) => item.programme),
                    tickLabelStyle: { fontSize: 12 },
                    width: 100,
                  },
                ]}
                series={[
                  {
                    data: deliveredData,
                    valueFormatter: (value: number | null) =>
                      value !== null ? `${value}%` : "",
                  },
                ]}
                margin={{ right: 0, left: 0, top: 0, bottom: 0 }}
                grid={{ vertical: true, horizontal: true }}
                borderRadius={4}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Programme</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Planned
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Delivered
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      %
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Benefit £m
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Risk £m
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row) => (
                    <TableRow key={row.programme} hover>
                      <TableCell>{row.programme}</TableCell>
                      <TableCell align="right">{row.plannedOutputs}</TableCell>
                      <TableCell align="right">{row.delivered}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              parseInt(row.percentDelivered) >= 70
                                ? "success.main"
                                : parseInt(row.percentDelivered) >= 50
                                ? "warning.main"
                                : "error.main",
                            fontWeight: 600,
                          }}
                        >
                          {row.percentDelivered}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.monetisedBenefit}
                      </TableCell>
                      <TableCell align="right">{row.riskReduction}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};
